import React, { useState } from 'react';
import axios from 'axios';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle
} from '../UI/card';
import { Button } from '../UI/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../UI/tabs';
import { Loader2, Upload, List, Eye } from 'lucide-react';
import FileUploader from './FileUploader';
import WithdrawalPreview from './WithdrawalPreview';
import WithdrawalSummary from './WithdrawalSummary';
import StreamProgressPanel from './StreamProgressPanel';
import { streamNdjson } from '../../services/streamNdjson';
import { useToast } from '../../hooks/use-toast';

/**
 * BULK WITHDRAWAL FLOW — Reprise des rachats FSR
 *
 * Même ergonomie que EnterpriseBulkPaymentFlowV2 :
 *   upload → prévisualisation (WithdrawalPreview) → traitement
 *   → résultats avec sections repliables et téléchargement Excel
 *     (WithdrawalSummary).
 *
 * Colonnes attendues : RAC_NUMRACHAT, ADH_MATRIC_SAL, ADH_NOM, ADH_PRENOMS,
 *   PASS_CODE, DAT_AVANCE, RAC_MONTANT_COTIS, RAC_INTERET, RAC_PENALITE,
 *   RAC_MONT_PAYE, RAC_DATECREATE, RAC_TYPE_RACHAT.
 */
const BulkWithdrawalFlow = ({ onCompleted }) => {
  const { toast } = useToast();
  const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

  const [activeTab, setActiveTab]   = useState('upload');
  const [file, setFile]             = useState(null);
  const [validation, setValidation] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [results, setResults]       = useState(null);
  const [progress, setProgress]     = useState(null);

  const postFile = async (endpoint, selectedFile) => {
    const token = localStorage.getItem('accessToken');
    const formData = new FormData();
    formData.append('file', selectedFile);
    return axios.post(`${API_URL}${endpoint}`, formData, {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'multipart/form-data'
      },
      timeout: 0
    });
  };

  const handleFileSelect = async (selectedFile) => {
    setFile(selectedFile);
    setValidation(null);
    try {
      setProcessing(true);
      const response = await postFile('/savings/withdrawals/validate-v2', selectedFile);
      setValidation(response.data.data.validation);
      setActiveTab('preview');
      toast({
        title: 'Fichier validé',
        description:
          `${response.data.data.validation.validRows} rachat(s) valide(s) sur ${response.data.data.validation.totalRows}`
      });
    } catch (error) {
      console.error('Erreur validation rachats:', error);
      toast({
        variant: 'destructive',
        title: 'Erreur',
        description:
          error.response?.data?.message || 'Erreur lors de la validation du fichier'
      });
      setFile(null);
    } finally {
      setProcessing(false);
    }
  };

  const handleFileRemove = () => {
    setFile(null);
    setValidation(null);
    setResults(null);
    setActiveTab('upload');
  };

  const handleProcess = async () => {
    if (!file || !validation) return;
    if (validation.validRows === 0) {
      toast({
        variant: 'destructive',
        title: 'Erreur',
        description: 'Aucun rachat valide à traiter'
      });
      return;
    }
    try {
      setProcessing(true);
      setProgress({
        current: 0, total: validation.validRows || 0, currentLabel: '',
        counters: { success: 0, errors: 0 }
      });

      const formData = new FormData();
      formData.append('file', file);

      const finalResult = await streamNdjson({
        url: `${API_URL}/savings/withdrawals/bulk-v2?stream=1`,
        formData,
        onEvent: (evt) => {
          if (evt.type === 'start') {
            setProgress((p) => ({ ...(p || {}), total: evt.total || p?.total || 0 }));
          } else if (evt.type === 'withdrawal') {
            setProgress((p) => ({
              ...(p || {}),
              current:      evt.current ?? p?.current ?? 0,
              total:        evt.total   ?? p?.total   ?? 0,
              currentLabel: evt.currentLabel || p?.currentLabel || '',
              counters: {
                success: evt.successCount ?? p?.counters?.success ?? 0,
                errors:  evt.errorCount   ?? p?.counters?.errors  ?? 0
              }
            }));
          }
        }
      });

      setResults(finalResult);
      setActiveTab('results');
      toast({
        title: 'Traitement terminé',
        description: `${finalResult?.successCount || 0} rachat(s) traité(s)`
      });
    } catch (error) {
      console.error('Erreur traitement rachats:', error);
      toast({
        variant: 'destructive',
        title: 'Erreur',
        description: error.message || 'Erreur lors du traitement des rachats'
      });
    } finally {
      setProcessing(false);
    }
  };

  const progressForPanel = progress ? {
    current:      progress.current,
    total:        progress.total,
    currentLabel: progress.currentLabel,
    metrics: [
      { label: 'Succès',  value: progress.counters?.success || 0, tone: 'emerald' },
      { label: 'Erreurs', value: progress.counters?.errors  || 0, tone: (progress.counters?.errors || 0) > 0 ? 'red' : 'gray' }
    ]
  } : null;

  return (
    <Tabs value={activeTab} onValueChange={setActiveTab}>
      <TabsList className="grid w-full grid-cols-3">
        <TabsTrigger value="upload" disabled={processing}>
          <Upload className="w-4 h-4 mr-2" />
          Upload
        </TabsTrigger>
        <TabsTrigger value="preview" disabled={!validation || processing}>
          <Eye className="w-4 h-4 mr-2" />
          Prévisualisation
        </TabsTrigger>
        <TabsTrigger value="results" disabled={!results}>
          <List className="w-4 h-4 mr-2" />
          Résultats
        </TabsTrigger>
      </TabsList>

      {/* Étape 1 : Upload */}
      <TabsContent value="upload" className="space-y-6 mt-4">
        <Card>
          <CardHeader>
            <CardTitle>Importer le fichier de rachats</CardTitle>
            <CardDescription>
              Colonnes attendues : <code>RAC_NUMRACHAT</code>, <code>ADH_MATRIC_SAL</code>,{' '}
              <code>RAC_MONTANT_COTIS</code>, <code>RAC_INTERET</code>,{' '}
              <code>RAC_PENALITE</code>, <code>RAC_MONT_PAYE</code>,{' '}
              <code>RAC_DATECREATE</code>, <code>RAC_TYPE_RACHAT</code>. Seul le produit{' '}
              <strong>FSR</strong> est pris en charge.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <FileUploader
              onFileSelect={handleFileSelect}
              onFileRemove={handleFileRemove}
              acceptedFormats=".csv,.xlsx"
              type="bulk"
              loading={processing}
            />
          </CardContent>
        </Card>
      </TabsContent>

      {/* Étape 2 : Prévisualisation */}
      <TabsContent value="preview" className="space-y-6 mt-4">
        <Card>
          <CardHeader>
            <CardTitle>Prévisualisation des rachats</CardTitle>
            <CardDescription>
              Vérifiez les données avant de lancer le traitement
            </CardDescription>
          </CardHeader>
          <CardContent>
            <WithdrawalPreview validationResults={validation} />

            {processing && progressForPanel && (
              <div className="mt-6">
                <StreamProgressPanel
                  title="Traitement des rachats"
                  progress={progressForPanel}
                  barColor="rose"
                />
              </div>
            )}

            <div className="flex gap-3 mt-6 pt-6 border-t">
              <Button
                variant="outline"
                onClick={() => setActiveTab('upload')}
                disabled={processing}
              >
                Retour
              </Button>
              <Button
                onClick={handleProcess}
                disabled={processing || !validation || validation.validRows === 0}
                className="flex-1"
              >
                {processing ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Traitement en cours...
                  </>
                ) : (
                  `Traiter ${validation?.validRows || 0} rachat(s)`
                )}
              </Button>
            </div>
          </CardContent>
        </Card>
      </TabsContent>

      {/* Étape 3 : Résultats */}
      <TabsContent value="results" className="space-y-6 mt-4">
        <WithdrawalSummary
          results={results}
          validationRows={validation?.rows || []}
        />

        <div className="flex gap-3">
          <Button
            variant="outline"
            onClick={handleFileRemove}
          >
            Nouveau traitement
          </Button>
          {onCompleted && (
            <Button onClick={() => onCompleted(results)}>
              Terminer
            </Button>
          )}
        </div>
      </TabsContent>
    </Tabs>
  );
};

export default BulkWithdrawalFlow;
